import { FC, useEffect } from "react";

import { useLogDir } from "../../../app_config";
import { kLogViewInfoTabId } from "../../../constants";
import {
  resolveLogKey,
  useLogFetchState,
  useLogHeader,
} from "../../../log_data";
import { useStore } from "../../../state/store";
import { useCurrentLogFile } from "../currentSelection";

/**
 * Reacts to the selected log's settled details — only the side effects that
 * can't be derived: resetting per-log
 * selection state, and defaulting the workspace tab for empty logs. All
 * fetching lives in the hook/engine. Rendered below the loader gate (by both
 * loader hosts) so the log dir the hook is keyed on is resolved before it
 * runs.
 *
 * The effect keys off `details_settled_seq`, not `detail.data` identity: the
 * details cache entry also receives poll-tick merges (running logs), which
 * must not re-run this effect. Only an ACTIVE waitered fetch settling bumps
 * the seq — `demand: "active"` here declares that (the other, passive
 * consumers of this same log — sample-adjacent hooks mounting elsewhere in
 * the tree — must not be able to refire this controller just by mounting).
 */
export const LogLoadController: FC = () => {
  const logDir = useLogDir();
  const selectedLogFile = useCurrentLogFile();
  const detail = useLogHeader(logDir, selectedLogFile, { demand: "active" });
  const key = selectedLogFile
    ? resolveLogKey(logDir, selectedLogFile)
    : undefined;
  const settledSeq = useLogFetchState(logDir, key).data?.details_settled_seq;

  const clearSelectedScores = useStore(
    (state) => state.logActions.clearSelectedScores
  );
  const setWorkspaceTab = useStore((state) => state.appActions.setWorkspaceTab);

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (
      !selectedLogFile ||
      detail.data === undefined ||
      settledSeq === undefined
    ) {
      return;
    }
    const details = detail.data;
    clearSelectedScores();
    if (details.status !== "started" && details.sampleCount === 0) {
      // If there are no samples, use the info tab by default
      setWorkspaceTab(kLogViewInfoTabId);
    }
    // detail.data is intentionally excluded: only a settled-seq bump should
    // refire this effect, not every poll-tick merge into the details cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledSeq, selectedLogFile, clearSelectedScores, setWorkspaceTab]);

  return null;
};
